import * as React from "react";
import {observer} from "mobx-react";
import {runInAction} from "mobx";
import type {MoveEvent, Sortable} from "react-sortablejs";
import {ReactSortable} from "react-sortablejs";
import styled from "styled-components";
import {ViewPreferenceCategory} from "../../../../generated/api/base";
import type {IViewFolder, ViewFolderStructure} from "../../../../data/models/ViewUtils";
import {
	cleanViewFolderStructureFromAdditionalProps,
	doesStructureContainElement,
	rootFolderId,
	isGlobalFolderSharedWithUser,
} from "../../../../data/models/ViewUtils";
import type {AppState} from "../../../../data/state/AppState";
import {ObjectUtils} from "../../../../utils/data/ObjectUtils";
import type {View} from "../../../../data/models/View";
import type {Report} from "../../../../data/models/Report";
import {baseDistance, fontSize} from "../../styles/styles";
import {ReactUtils} from "../../../utils/ReactUtils";
import {Functions} from "../../../../utils/function/Functions";
import type {ViewActionType} from "../../sharing/GlobalSharingPopup";
import {ViewFolderClassName, ViewFolderV5} from "./ViewFolderV5";
import {ViewItemV5} from "./ViewItemV5";
import type {OnWorkspaceRenameActiveChange, WorkspaceViewType} from "./WorkspaceViewCommon";

interface IViewFolderStructureProps {
	readonly appState: AppState;
	readonly type: WorkspaceViewType;
	readonly level: number;
	readonly viewFolder: IViewFolder;
	readonly onViewClick: (view: View | Report) => void;
	readonly saveViewFolderStructureToDatabase: () => void;
	readonly removeViewElementFromStructure: (viewElementId: string) => boolean;
	readonly forceUpdateParent: () => void;
	readonly onShareClick: (viewItem: View | IViewFolder | Report) => void;
	readonly onGlobalViewActionClick: (view: View | Report, viewActionType: ViewActionType, newName?: string) => void;
	readonly onViewPopupOpenChange: (isOpen: boolean) => void;
	readonly onOpenReportPanel: (report: Report) => void;
	readonly onWorkspaceRenameActiveChange: OnWorkspaceRenameActiveChange;
	readonly onAddClick?: (viewFolderId: string) => void;
	readonly searchString: string;
	readonly lastElementInList: React.RefObject<ViewFolderV5>;
}

const indentationSize = 16;
const dragTolerance = 4;

class ViewFolderStructureReactV5Component extends React.Component<IViewFolderStructureProps> {
	// TODO: Probably when this returns with true, sortable.js calls event.preventDefault,
	// So it's not possible right now to change the text cursor (caret), or select the text
	// within the input element with draggable parents...
	private filterDragElement(this: Sortable, event: Event | TouchEvent, target: HTMLElement, sortable: Sortable): boolean {
		const element = event.target as HTMLElement;
		const elementName = element.nodeName.toLowerCase();

		return elementName === "input" || elementName === "textarea";
	}

	private setViewFolderStructure = (newViewFolderStructure: ViewFolderStructure) => {
		runInAction(() => {
			if (
				!ObjectUtils.compare(
					cleanViewFolderStructureFromAdditionalProps(this.props.viewFolder.children),
					cleanViewFolderStructureFromAdditionalProps(newViewFolderStructure),
				)
			) {
				this.props.viewFolder.children.length = 0;
				this.props.viewFolder.children.push(...newViewFolderStructure);

				return this.props.saveViewFolderStructureToDatabase();
			}
		});
	};

	private getMarginLeftForLevel(level: number) {
		return `${indentationSize * Math.sign(level)}px`;
	}

	public override render() {
		const {
			viewFolder,
			saveViewFolderStructureToDatabase,
			level,
			onShareClick,
			removeViewElementFromStructure,
			onAddClick,
			type,
			appState,
			onViewClick,
			onGlobalViewActionClick,
			onViewPopupOpenChange,
			onOpenReportPanel,
			onWorkspaceRenameActiveChange,
			forceUpdateParent,
			searchString,
			lastElementInList,
		} = this.props;

		if (viewFolder.children?.length === 0 && viewFolder.id === "root") {
			return null;
		}

		const {children} = viewFolder;
		const filteredChildren = searchString
			? viewFolder.children.filter((v) => doesStructureContainElement([v].flat(), searchString, appState.actions))
			: viewFolder.children;
		const isOpenFinal = viewFolder.isOpen || (searchString && doesStructureContainElement(viewFolder.children, searchString, appState.actions));
		const isFolderVisible = type === "Global" ? isGlobalFolderSharedWithUser(viewFolder, appState, rootFolderId) : true;

		return (
			<div>
				{viewFolder.id !== rootFolderId && isFolderVisible && (
					<ViewFolderV5
						data={viewFolder}
						onShareClick={onShareClick}
						saveViewFolderStructureToDatabase={saveViewFolderStructureToDatabase}
						removeViewElementFromStructure={removeViewElementFromStructure}
						onAddClick={onAddClick}
						ref={level === 1 ? (lastElementInList as React.RefObject<ViewFolderV5>) : undefined}
						type={type}
						searchString={searchString}
						onViewPopupOpenChange={onViewPopupOpenChange}
						onWorkspaceRenameActiveChange={onWorkspaceRenameActiveChange}
					/>
				)}
				<ReactSortable
					className={ReactUtils.cls({ReactSortable: !(viewFolder.isOpen && filteredChildren.length === 0), root: viewFolder.id === "root"})}
					list={children}
					setList={this.setViewFolderStructure}
					group={`${type}_nested`}
					animation={150}
					fallbackOnBody={true}
					forceFallback={true}
					swapThreshold={0.45}
					fallbackTolerance={dragTolerance}
					touchStartThreshold={dragTolerance}
					preventOnFilter={false}
					onMove={(event: MoveEvent) => {
						//
						// LIMIT DEPTH
						//
						if (type === "Favorites" || (type === "Global" && appState.user.isAdmin)) {
							const isFolder = event.dragged.children?.[0].classList.contains(ViewFolderClassName);
							// className "root" added to root folder, if the event.to has NO "root", the user tries to drop item into a folder
							const intoFolder = !event.to.className.includes("root");

							if (intoFolder && isFolder) {
								return false;
							}

							return true;
						}
					}}
					delay={500}
					delayOnTouchOnly={true}
					style={{
						paddingLeft: this.getMarginLeftForLevel(level),
						marginBottom: "0px", // to be able to drop an item below the folder (and not inside)
						display: "flex",
						flexDirection: "column",
						gap: "0px",
						paddingBottom:
							level === 0 && filteredChildren.length > 1 && filteredChildren[filteredChildren.length - 1].category === ViewPreferenceCategory.Folder
								? baseDistance.sm
								: "0",
					}}
					dragClass="dragged"
					filter={this.filterDragElement}
					disabled={level > 1 || (type === "Global" && !appState.user.isAdmin)}
				>
					{isOpenFinal && isFolderVisible && filteredChildren.length === 0 ? (
						<ViewContainerStyled onClick={Functions.stopPropagation}>
							<span>There are no views in the folder</span>
						</ViewContainerStyled>
					) : (
						isOpenFinal &&
						isFolderVisible &&
						filteredChildren.length > 0 &&
						filteredChildren.map((v) => {
							if (v.category === ViewPreferenceCategory.View || v.category === ViewPreferenceCategory.Report) {
								const viewMaybe = appState.actions.getViewOrReportViewById(v.id);

								if (!viewMaybe) {
									console.warn(`Current user doesn't have access to view with id ${v.id}, or view doesn't exist anymore`);

									return <span key={`no-access-${v.id}-${this.props.type}`} />;
								}

								return (
									<ViewItemV5
										key={`${v.id}-${this.props.type}`}
										view={viewMaybe}
										type={type}
										onViewClick={onViewClick}
										onShareClick={onShareClick}
										onGlobalViewActionClick={onGlobalViewActionClick}
										onViewPopupOpenChange={onViewPopupOpenChange}
										onOpenReportPanel={onOpenReportPanel}
										onWorkspaceRenameActiveChange={onWorkspaceRenameActiveChange}
										forceUpdateParent={forceUpdateParent}
										level={level}
										searchString={searchString}
									/>
								);
							} else {
								return (
									<ViewFolderStructureReactV5
										key={`${v.id}-${this.props.type}`}
										appState={appState}
										viewFolder={v as IViewFolder}
										level={level + 1}
										saveViewFolderStructureToDatabase={saveViewFolderStructureToDatabase}
										type={type}
										removeViewElementFromStructure={removeViewElementFromStructure}
										onAddClick={onAddClick}
										onViewClick={onViewClick}
										onShareClick={onShareClick}
										onGlobalViewActionClick={onGlobalViewActionClick}
										onViewPopupOpenChange={onViewPopupOpenChange}
										onOpenReportPanel={onOpenReportPanel}
										onWorkspaceRenameActiveChange={onWorkspaceRenameActiveChange}
										forceUpdateParent={forceUpdateParent}
										searchString={searchString}
										lastElementInList={this.props.lastElementInList}
									/>
								);
							}
						})
					)}
				</ReactSortable>
			</div>
		);
	}
}

export const ViewFolderStructureReactV5 = observer(ViewFolderStructureReactV5Component);

const ViewContainerStyled = styled.span`
	display: flex;
	cursor: default;
	flex-direction: column;
	gap: ${baseDistance.sm};
	font-size: ${fontSize.md};
	line-height: 16px;
	margin-left: 24px;
`;
